import type { TFunction } from '!/i18n'
import {
  checkBundlePurchase,
  checkUpdatedItemIds,
  extendMediaItemByLabelPriceTvShowEpisode,
  getProgress,
} from '@/tools'
import type {
  CurrentTvShow,
  TvShowEpisode,
  TvShowSeason,
} from '@setplex/tria-api'
import {
  type AdjustPurchaseableFn,
  type SseIds,
} from '~/features/payment/sse-purchases'
import {
  DATE_FORMATS,
  formatDateDuration,
  formatStartTime,
  timeWithin,
} from '~/shared/helpers/date'
import type { ITvShowEpisodeFormat } from './index.h'

export const MAX_PROGRESS_IN_PERCENTS = 100

export const formatProgress = ({
  duration,
  stoppedTime,
  watched,
}: {
  duration?: number | null
  stoppedTime?: number | null
  watched?: boolean
}): number | null => {
  if (!stoppedTime && watched) return MAX_PROGRESS_IN_PERCENTS

  return getProgress({
    duration,
    stoppedTime,
  })
}

export const formatTVShowEpisode = (
  episodes: TvShowEpisode[],
  adjust: AdjustPurchaseableFn,
  { t }: { t: TFunction }
): ITvShowEpisodeFormat[] => {
  return episodes.map((episode) => {
    const { duration, stoppedTime, watched, sortOrder, releaseTime } = episode

    const count = Number(sortOrder) + 1
    const formattedSortOrder = t('episode_number', {
      number: count,
    })

    const { rented, labelPrice } = extendMediaItemByLabelPriceTvShowEpisode(
      adjust(episode)
    )
    const progress = formatProgress({ duration, stoppedTime, watched })
    const { hours, minutes } = timeWithin(duration || 0)
    const formattedDuration = formatDateDuration({
      duration: { hours, minutes },
      short: false,
    })

    return {
      ...episode,
      releaseTimeFormatted: releaseTime
        ? formatStartTime(
            releaseTime * 1000,
            DATE_FORMATS.MMM_DD_YYYY_LOCALIZED
          )
        : '',
      length: formattedDuration,
      formattedSortOrder,
      rented,
      labelPrice,
      progress,
    }
  })
}

export const formatTVShowSeasonEpisodesLabel = (
  episodes: ITvShowEpisodeFormat[] | null,
  { t }: { t: TFunction }
) => {
  const count = episodes?.length

  return count
    ? t('episode_count', {
        count,
      })
    : ''
}

export const formatTVShowEpisodesLabel = (
  tvShow: CurrentTvShow | null,
  { t }: { t: TFunction }
) => {
  return tvShow?.episodeCount
    ? t('episode_count', {
        count: tvShow.episodeCount,
      })
    : ''
}

export const formatTVShowSeasonsLabel = (
  tvShow: CurrentTvShow | null,
  { t }: { t: TFunction }
) => {
  return tvShow?.seasonCount
    ? t('season_count', {
        count: tvShow.seasonCount,
      })
    : ''
}

export const checkUpdatedItem = ({
  currentShow,
  season,
  ids,
}: {
  currentShow: CurrentTvShow | null
  season: TvShowSeason | null
  ids: SseIds
}) => {
  const checkUpdatedItem = checkUpdatedItemIds(ids)

  return checkUpdatedItem(season) || checkUpdatedItem(currentShow)
}

export const checkUpdatedItemBundleIds = ({
  currentShow,
  season,
  ids,
}: {
  currentShow: CurrentTvShow | null
  season: TvShowSeason | null
  ids: SseIds
}) => {
  const currentShowUpdated = checkBundlePurchase({
    item: currentShow,
    priceSettings: currentShow?.priceSettings || [],
    bundleIds: ids,
  })

  const seasonUpdated = checkBundlePurchase({
    item: season,
    priceSettings: [
      ...(season?.tvShowPriceSettings || []),
      ...(season?.seasonPriceSettings || []),
    ],
    bundleIds: ids,
  })

  return currentShowUpdated || seasonUpdated
}
